/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import Layout from "@/components/Layout";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  PlayCircle,
  RefreshCcw,
  Zap,
} from "lucide-react";
import {
  useApiFootballAudit,
  useApiFootballReingest,
  useApiFootballTickNow,
} from "@/hooks/api/useApiFootball";
import type { ApiFootballAuditIssue } from "@/api/services/apiFootball.service";

/**
 * Admin tool that walks every COMPLETED match and reports gaps between
 * api-football's event timeline and our MatchEvent table. Surfaces
 * unmapped player ids — those are the ones to manually link before
 * re-ingesting.
 *
 * Designed to be run periodically (e.g. after each matchday). The first
 * audit on a fresh deployment can take 30-60 seconds because it pulls
 * one /fixtures/events call per completed match (~100 in the full
 * tournament). Subsequent runs are cached client-side via React Query
 * with staleTime=0 so re-clicking always fetches fresh.
 */
export default function ApiFootballAudit() {
  // Audit query is disabled until the user clicks "Run Audit" — first
  // click flips this to true; thereafter refetches happen on demand.
  const [enabled, setEnabled] = useState(false);

  const { data, isFetching, isError, error, refetch } =
    useApiFootballAudit(enabled);
  const { mutate: reingest, isPending: isReingesting, variables: reingestingId } =
    useApiFootballReingest();
  const { mutate: tickNow, isPending: isTicking } = useApiFootballTickNow();

  const handleTickNow = () => {
    tickNow(undefined, {
      onSuccess: (resp: any) => {
        // Backend wraps internal errors as { ok: false, error, stack }
        // with HTTP 200 so the mutation lands here. Inspect the body.
        if (resp?.ok === false) {
          toast.error(
            `Tick failed: ${resp.error ?? "unknown error"}`,
            { autoClose: 10000 },
          );
          // Helpful for diagnostics — full payload in console.
          console.error("tick-now error response", resp);
          return;
        }
        const pollable = resp?.pollable ?? 0;
        const newEvents = resp?.totalNewEvents ?? 0;
        if (pollable === 0) {
          toast.info(
            "Tick fired — no matches currently in the ±6h kickoff window",
          );
        } else if (newEvents === 0) {
          toast.success(
            `Tick fired — polled ${pollable} match(es), no new events`,
          );
        } else {
          toast.success(
            `Tick fired — polled ${pollable} match(es), wrote ${newEvents} new event(s)`,
          );
        }
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.message ?? err?.message ?? "Tick failed",
        );
      },
    });
  };

  const handleRunAudit = () => {
    if (!enabled) {
      setEnabled(true); // first-time enable triggers the query
    } else {
      refetch(); // subsequent presses just refresh
    }
  };

  const handleReingest = (issue: ApiFootballAuditIssue) => {
    reingest(issue.matchId, {
      onSuccess: (summary) => {
        toast.success(
          `#${issue.matchId} re-ingested — ${summary.eventsInserted} new event(s) inserted`,
        );
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            `Failed to re-ingest match ${issue.matchId}`,
        );
      },
    });
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          API-Football Coverage Audit
        </h1>
        <p className="text-gray-500 mt-1">
          Verify that every completed match has the same goals, assists, and
          cards in our DB that api-football reports. Surfaces matches where
          an event was skipped because the scorer / carded player isn't
          linked in our DB.
        </p>
      </div>

      <div className="space-y-6">
        {/* Tick-now button — forces the cron to run now instead of
            waiting for the next 5-min boundary. Use when a live match
            just had a goal/card and you want the ingester to pick it
            up immediately. */}
        <div className="flex items-start gap-4 rounded-lg border border-amber-200 bg-amber-50 p-6">
          <Zap className="h-5 w-5 text-amber-600 shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">
              Force ingestion cron to run now
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Triggers the same job that runs every 5 min — fetches
              current status + new events for every match in the ±6h
              kickoff window and writes them to the DB. Use when a live
              match event needs to land immediately.
            </p>
          </div>
          <Button
            onClick={handleTickNow}
            disabled={isTicking}
            variant="outline"
            className="gap-2 shrink-0"
          >
            {isTicking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Zap className="h-4 w-4" />
            )}
            {isTicking ? "Ticking…" : "Run cron now"}
          </Button>
        </div>

        {/* Run button + summary */}
        <div className="flex items-center gap-4 rounded-lg border bg-white p-6">
          <Button
            onClick={handleRunAudit}
            disabled={isFetching}
            size="lg"
            className="gap-2"
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : enabled ? (
              <RefreshCcw className="h-4 w-4" />
            ) : (
              <PlayCircle className="h-4 w-4" />
            )}
            {isFetching
              ? "Auditing…"
              : enabled
                ? "Re-run audit"
                : "Run audit"}
          </Button>

          {data && !isFetching && (
            <div className="flex-1 text-sm text-gray-600">
              <span className="font-medium">
                {data.affectedMatches}
              </span>{" "}
              of {data.totalCompletedMatches} completed match(es) have gaps.
              {data.affectedMatches === 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  All clear.
                </span>
              )}
              <div className="text-xs text-gray-400 mt-1">
                Last run: {new Date(data.ranAt).toLocaleString()}
              </div>
            </div>
          )}

          {!enabled && (
            <div className="flex-1 text-sm text-gray-500">
              Click <span className="font-medium">Run audit</span> to scan
              every completed match for event-coverage gaps. Costs ~1
              api-football call per match (well within Pro plan budget).
            </div>
          )}
        </div>

        {/* Error state */}
        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            <AlertCircle className="inline h-4 w-4 mr-2" />
            Audit failed: {(error as any)?.message ?? "unknown error"}
          </div>
        )}

        {/* Empty (no gaps) state — already shown inline above; results table only when there are issues */}
        {data && data.issues.length > 0 && (
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Match</TableHead>
                  <TableHead>Fixture</TableHead>
                  <TableHead className="w-[80px]">Score</TableHead>
                  <TableHead className="w-[90px] text-center">
                    Missed goals
                  </TableHead>
                  <TableHead className="w-[100px] text-center">
                    Missed assists
                  </TableHead>
                  <TableHead className="w-[90px] text-center">
                    Missed cards
                  </TableHead>
                  <TableHead>Unmapped api player ids</TableHead>
                  <TableHead className="w-[140px] text-right">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.issues.map((issue) => {
                  const isThisRowReingesting =
                    isReingesting && reingestingId === issue.matchId;
                  return (
                    <TableRow key={issue.matchId}>
                      <TableCell className="font-mono text-xs">
                        #{issue.matchId}
                      </TableCell>
                      <TableCell className="font-medium">
                        {issue.label}
                      </TableCell>
                      <TableCell className="font-mono">
                        {issue.score}
                      </TableCell>
                      <TableCell className="text-center">
                        {issue.missedGoals > 0 ? (
                          <span className="font-semibold text-red-600">
                            {issue.missedGoals}
                          </span>
                        ) : (
                          <span className="text-gray-300">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {issue.missedAssists > 0 ? (
                          <span className="font-semibold text-amber-600">
                            {issue.missedAssists}
                          </span>
                        ) : (
                          <span className="text-gray-300">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {issue.missedCards > 0 ? (
                          <span className="font-semibold text-gray-700">
                            {issue.missedCards}
                          </span>
                        ) : (
                          <span className="text-gray-300">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {issue.unmappedApiPlayerIds.length === 0 ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <span className="font-mono">
                            {issue.unmappedApiPlayerIds.slice(0, 6).join(", ")}
                            {issue.unmappedApiPlayerIds.length > 6 &&
                              ` (+${issue.unmappedApiPlayerIds.length - 6} more)`}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReingest(issue)}
                          disabled={isReingesting}
                          className="gap-1"
                        >
                          {isThisRowReingesting ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RefreshCcw className="h-3 w-3" />
                          )}
                          Re-ingest
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className="border-t bg-gray-50 px-4 py-3 text-xs text-gray-500">
              <p>
                <strong>To fix unmapped players:</strong> look up the api
                player id on api-football.com, create the Player in the
                Players admin (set their <code>apiFootballPlayerId</code>{" "}
                while you're at it), then click Re-ingest on the affected
                match. The MatchEvent unique constraint dedupes existing
                rows — only the previously-skipped events get added.
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
